import { Data, UserData } from "../../../../types/org.type";
import { resolveCountryCode } from "@funded-here-interface/common/src/constant/country";
import { codeToCountryNameMap } from "@funded-here-interface/common/src/constant/country";
import { mapCountryToCurrencyCode } from "../Components/currencyCode";
import { deduplicateCountries } from "../Components/utils";
import {
  mapEntityType,
  mapIndustryToBusinessType,
  mapIndustryToEmploymentIndustry,
} from "../data-mapping";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import { splitPhoneNumber } from "../../../../Utils/phoneUtils";
import { WallexUserInfoPayload } from "../Service/ApiServices";

interface DerivedValuesInput {
  data: Data | undefined;
  userData: UserData | undefined;
  isOrgRoute: boolean;
  isIndividualRoute: boolean;
}

export interface WallexDerivedValues {
  // Country
  countryOfResidence: string;

  // Individual
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  mobileCountryCode: string;
  mobileNumber: string;
  employmentStatus: string;
  employmentIndustry: string;
  employmentPosition: string;
  dateOfBirth: string;
  countryOfBirth: string;
  address: string;
  postalCode: string;
  countryCode: string;
  nationality: string;
  identificationType: string;
  identificationNumber: string;
  issueDate: string;
  expiryDate: string;

  // Company
  companyName: string;
  registrationNumber: string;
  businessType: string;
  incorporationDate: string;
  displayIncorporationDate: string;
  city: string;
  state: string;
  countryOfDomicile: string;
  countryOfIncorporation: string;
  customerBusinessWebsite: string;
  customerBusinessDescription: string;
  entityType: string;
  ownershipStructureLayers: string;
  countryOfOperation: string;

  // Currency
  currencyCode: string;
  currencyCodeForDomicile: string;

  // Due diligence
  paymentModes: string;
  onboardingMode: string;
  productServiceComplexity: string;
  companyAddress: string;
  companyPostalCode: string;
  isPermanentPlaceOfBusiness: string;
  siteVisitPerformedAtAddress: string;
  isPremisesInLineWithBusiness: string;
  isHigherRiskEntityType: string;
  higherRiskEntityType: string;
  productUsage: string;
  purposeOfAccountOpening: string;
  isMakingPaymentsToIndividuals: string;
  makingPaymentsToIndividualsDescription: string;
  isMakingThirdPartyPayments: string;
  sourceOfFunds: string;

  // Transacting details
  primaryTransactingCountriesInbound: string;
  primaryTransactingCountriesOutbound: string;
  topCounterPartiesInbound: string;
  topCounterPartiesOutbound: string;
  anticipatedVolumeMonthlyTransactionsCountInbound: string;
  anticipatedVolumeMonthlyTransactionsCountOutbound: string;
  anticipatedValueMonthlyTransactionsInbound: string | number;
  anticipatedValueMonthlyTransactionsOutbound: string | number;
  currencyPairsInbound: string;
  currencyPairsOutbound: string;
}

export const buildDerivedValues = ({
  data,
  userData,
  isOrgRoute,
  isIndividualRoute,
}: DerivedValuesInput): WallexDerivedValues => {
  // Country of residence
  const countryOfResidence =
    codeToCountryNameMap.get(data?.countryISOCode || "") || "-";

  // Individual fields
  const firstName = isOrgRoute ? "Guofeng" : userData?.firstName || "-";
  const lastName = isOrgRoute ? "Chang" : userData?.lastName || "-";
  const email = isOrgRoute
    ? "guofeng.chang@fundedhere.com"
    : userData?.email || "-";
  const gender = isOrgRoute ? "male" : data?.gender.toLocaleLowerCase() || "-";

  const phoneData = splitPhoneNumber(
    isOrgRoute ? "+60177199872" : data?.companyPhoneNumber
  );
  const mobileCountryCode = phoneData.countryCode;
  const mobileNumber = phoneData.number;
  const employmentStatus = isOrgRoute
    ? "Employed"
    : data?.employmentStatus || "-";
  const employmentIndustry = isOrgRoute
    ? "Banking/Finance/Insurance"
    : mapIndustryToEmploymentIndustry(data?.industry) || "-";
  const employmentPosition = isOrgRoute
    ? "Senior Level/Executive"
    : data?.employmentPosition || "-";
  const dateOfBirth = isOrgRoute
    ? "03-01-1994"
    : convertDate(String(data?.dateOfBirth), "DD-MM-YYYY") || "-";

  const countryOfBirth = isOrgRoute
    ? "MY"
    : resolveCountryCode(data?.countryOfBirth);
  const address = isOrgRoute
    ? "No. 43, Jalan Intan Baiduri 3D, Taman Intan Baiduri, Kuala Lumpur"
    : data?.legalEntityAddress || "-";
  const postalCode = isOrgRoute ? "52100" : data?.postalCode || "-";
  const countryCode = isIndividualRoute
    ? resolveCountryCode(countryOfResidence)
    : "MY";
  const nationality = isIndividualRoute
    ? resolveCountryCode(data?.nationality)
    : "MY";
  const identificationType = isOrgRoute ? "passport" : "national_identity";
  const identificationNumber = isOrgRoute
    ? "A56440609"
    : (userData?.regtankRequest !== undefined &&
        userData?.regtankRequest.length > 0 &&
        userData?.regtankRequest?.[0].ocrResults.idNumber) ||
      "-";
  const issueDate = isOrgRoute ? "18-07-2022" : dateOfBirth;
  const expiryDate = isOrgRoute ? "24-10-2027" : "-";

  // Company fields
  const companyName = isOrgRoute ? data?.companyName || "-" : "-";
  const registrationNumber = data?.companyRegistrationNo || "-";
  const businessType = mapIndustryToBusinessType(data?.industry) || "-";
  const incorporationDate = data?.companyStartDate || "-";
  const displayIncorporationDate =
    convertDate(String(data?.companyStartDate), "DD-MM-YYYY") || "-";
  const city = "-";
  const state = "-";
  const countryOfDomicile = data?.countryISOCode || "-";
  const countryOfIncorporation = resolveCountryCode(
    data?.countryOfIncorporation
  );
  const customerBusinessWebsite = data?.website || "-";
  const customerBusinessDescription =
    userData?.entityType === "institution" ? "investor" : "e-commerce merchant";
  const entityType = mapEntityType(data?.legalStructure) || "-";
  const ownershipStructureLayers = "1";
  const countryOfOperation = resolveCountryCode(countryOfDomicile);

  // Currency
  const currencyCode = mapCountryToCurrencyCode(data?.countryOfIncorporation);
  const currencyCodeForDomicile = mapCountryToCurrencyCode(
    codeToCountryNameMap.get(countryOfDomicile)
  );

  // Due diligence
  const paymentModes = "cash_direct_debit_or_credit";
  const onboardingMode = "face_to_face";
  const productServiceComplexity = "simple";
  const companyAddress = data?.legalEntityAddress || "-";
  const companyPostalCode = data?.postalCode || "-";
  const isPermanentPlaceOfBusiness = "yes";
  const siteVisitPerformedAtAddress = "perform virtually";
  const isPremisesInLineWithBusiness = "yes";
  const isHigherRiskEntityType =
    userData?.entityType === "institution" ? "no" : "yes";
  const higherRiskEntityType =
    userData?.entityType === "institution" ? "-" : "e_commerce_platform";
  const productUsage =
    "inbound_cross_border_or_collections, outbound_cross_border";
  const purposeOfAccountOpening =
    userData?.entityType === "institution"
      ? "investment"
      : "payment and collection";
  const isMakingPaymentsToIndividuals = "no";
  const makingPaymentsToIndividualsDescription = "-";
  const isMakingThirdPartyPayments = "no";
  const sourceOfFunds = "first_party_only";

  // Transacting details
  const countryOfIncorporationName =
    codeToCountryNameMap.get(countryOfIncorporation) || "-";
  const countryOfDomicileName =
    codeToCountryNameMap.get(countryOfDomicile) || "-";

  const primaryTransactingCountriesInbound = deduplicateCountries(
    userData?.entityType === "institution"
      ? `${countryOfIncorporationName},${countryOfDomicileName},Singapore`
      : countryOfDomicileName
  );
  const primaryTransactingCountriesOutbound = deduplicateCountries(
    userData?.entityType === "institution"
      ? `${countryOfIncorporationName},${countryOfDomicileName},Singapore`
      : `${countryOfDomicileName},Singapore`
  );
  const topCounterPartiesInbound =
    userData?.entityType === "institution"
      ? "Funded Here Pte. Ltd."
      : "e-commerce platform";
  const topCounterPartiesOutbound = "Funded Here Pte. Ltd.";
  const anticipatedVolumeMonthlyTransactionsCountInbound = "20";
  const anticipatedVolumeMonthlyTransactionsCountOutbound =
    userData?.entityType === "institution" ? "10" : "50";
  const anticipatedValueMonthlyTransactionsInbound =
    userData?.entityType === "institution"
      ? data?.investmentPreferences?.investmentSize || "-"
      : userData?.orgRole?.[0]?.creditLimit || "-";
  const anticipatedValueMonthlyTransactionsOutbound =
    userData?.entityType === "institution"
      ? data?.investmentPreferences?.investmentSize || "-"
      : userData?.orgRole?.[0]?.creditLimit || "-";

  const currencyPairsInboundSetOrg = new Set([`SGD${currencyCode}`, "SGDSGD"]);
  const currencyPairsInbound =
    userData?.entityType === "institution"
      ? Array.from(currencyPairsInboundSetOrg).join(", ")
      : `${currencyCodeForDomicile}${currencyCodeForDomicile}`;

  const currencyPairsOutboundSetOrg = new Set([`SGD${currencyCode}`, "SGDSGD"]);
  const currencyPairsOutboundSetInd = new Set([
    `${currencyCodeForDomicile}${currencyCodeForDomicile}`,
    `SGD${currencyCodeForDomicile}`,
  ]);
  const currencyPairsOutbound =
    userData?.entityType === "institution"
      ? Array.from(currencyPairsOutboundSetOrg).join(", ")
      : Array.from(currencyPairsOutboundSetInd).join(", ");

  return {
    countryOfResidence,
    firstName,
    lastName,
    email,
    gender,
    mobileCountryCode,
    mobileNumber,
    employmentStatus,
    employmentIndustry,
    employmentPosition,
    dateOfBirth,
    countryOfBirth,
    address,
    postalCode,
    countryCode,
    nationality,
    identificationType,
    identificationNumber,
    issueDate,
    expiryDate,
    companyName,
    registrationNumber,
    businessType,
    incorporationDate,
    displayIncorporationDate,
    city,
    state,
    countryOfDomicile,
    countryOfIncorporation,
    customerBusinessWebsite,
    customerBusinessDescription,
    entityType,
    ownershipStructureLayers,
    countryOfOperation,
    currencyCode,
    currencyCodeForDomicile,
    paymentModes,
    onboardingMode,
    productServiceComplexity,
    companyAddress,
    companyPostalCode,
    isPermanentPlaceOfBusiness,
    siteVisitPerformedAtAddress,
    isPremisesInLineWithBusiness,
    isHigherRiskEntityType,
    higherRiskEntityType,
    productUsage,
    purposeOfAccountOpening,
    isMakingPaymentsToIndividuals,
    makingPaymentsToIndividualsDescription,
    isMakingThirdPartyPayments,
    sourceOfFunds,
    primaryTransactingCountriesInbound,
    primaryTransactingCountriesOutbound,
    topCounterPartiesInbound,
    topCounterPartiesOutbound,
    anticipatedVolumeMonthlyTransactionsCountInbound,
    anticipatedVolumeMonthlyTransactionsCountOutbound,
    anticipatedValueMonthlyTransactionsInbound,
    anticipatedValueMonthlyTransactionsOutbound,
    currencyPairsInbound,
    currencyPairsOutbound,
  };
};

export const buildWallexPayload = (
  v: WallexDerivedValues,
  userId: string | number | undefined,
  isIndividualRoute: boolean,
  excludedFiles: string[]
): WallexUserInfoPayload => ({
  userIdFH: String(userId ?? ""),
  accountType: isIndividualRoute ? "individual" : "company",
  email: v.email,
  firstName: String(v.firstName),
  lastName: String(v.lastName),
  countryCode: v.countryCode,
  companyName: v.companyName,

  gender: String(v.gender),
  mobileCountryCode: String(v.mobileCountryCode),
  mobileNumber: Number(v.mobileNumber) || 0,
  employmentStatus: String(v.employmentStatus),
  employmentIndustry: String(v.employmentIndustry),
  employmentPosition: String(v.employmentPosition),
  dateOfBirth: String(v.dateOfBirth),
  countryOfBirth: String(v.countryOfBirth),
  residentialAddress: String(v.address),
  postalCode: String(v.postalCode),
  nationality: String(v.nationality),
  identificationType: v.identificationType,
  identificationNumber: v.identificationNumber,
  issueDate: v.issueDate,
  expiryDate: v.expiryDate,

  registrationNumber: v.registrationNumber,
  businessType: v.businessType,
  incorporationDate: v.incorporationDate,
  city: v.city,
  state: v.state,
  companyAddress: String(v.companyAddress),
  companyPostalCode: String(v.companyPostalCode),

  customerBusinessWebsite: v.customerBusinessWebsite,
  customerBusinessDescription: v.customerBusinessDescription,
  entityType: v.entityType,
  ownershipStructureLayers: v.ownershipStructureLayers,
  countryOfIncorporation: v.countryOfIncorporation,
  countryOfOperations: v.countryOfOperation,
  paymentModes: v.paymentModes,
  onboardingMode: v.onboardingMode,
  productServiceComplexity: v.productServiceComplexity,
  isPermanentPlaceOfBusiness: v.isPermanentPlaceOfBusiness,
  siteVisitPerformedAtAddress: v.siteVisitPerformedAtAddress,
  isPremisesInLineWithBusiness: v.isPremisesInLineWithBusiness,
  isHigherRiskEntityType: v.isHigherRiskEntityType,
  higherRiskEntityType: v.higherRiskEntityType,
  productUsage: v.productUsage,
  purposeOfAccountOpening: v.purposeOfAccountOpening,
  isMakingPaymentsToIndividuals: v.isMakingPaymentsToIndividuals,
  makingPaymentsToIndividualsDescription:
    v.makingPaymentsToIndividualsDescription,
  isMakingThirdPartyPayments: v.isMakingThirdPartyPayments,
  sourceOfFunds: v.sourceOfFunds,
  primaryTransactingCountriesInbound: v.primaryTransactingCountriesInbound,
  primaryTransactingCountriesOutbound: v.primaryTransactingCountriesOutbound,
  topCounterPartiesInbound: v.topCounterPartiesInbound,
  topCounterPartiesOutbound: v.topCounterPartiesOutbound,
  anticipatedVolumeMonthlyTransactionsCountInbound:
    parseInt(v.anticipatedVolumeMonthlyTransactionsCountInbound, 10) || 0,
  anticipatedVolumeMonthlyTransactionsCountOutbound:
    parseInt(v.anticipatedVolumeMonthlyTransactionsCountOutbound, 10) || 0,
  anticipatedValueMonthlyTransactionsInbound:
    parseInt(v.anticipatedValueMonthlyTransactionsInbound?.toString(), 10) || 0,
  anticipatedValueMonthlyTransactionsOutbound:
    parseInt(v.anticipatedValueMonthlyTransactionsOutbound?.toString(), 10) || 0,
  currencyPairsInbound: v.currencyPairsInbound,
  currencyPairsOutbound: v.currencyPairsOutbound,
  excludedFiles,
});
